<template>
  <footer class="footer-mobile">
    <!-- Trust Badges Section - Always Visible -->
    <div class="trust-section">
      <div class="container mx-auto px-4 py-6">
        <div class="flex flex-wrap justify-center gap-2 mb-4">
          <div class="trust-badge-mobile">
            <Icon icon="mdi:shield-check" width="14" height="14" />
            <span>Licensed & Bonded</span>
          </div>
          <div class="trust-badge-mobile">
            <Icon icon="mdi:security" width="14" height="14" />
            <span>Secure & Protected</span>
          </div>
          <div class="trust-badge-mobile">
            <Icon icon="mdi:office-building-cog" width="14" height="14" />
            <span>Multiple Carriers</span>
          </div>
          <div class="trust-badge-mobile">
            <Icon icon="mdi:map-outline" width="14" height="14" />
            <span>Nationwide</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Visual Divider -->
    <div class="section-divider"></div>

    <!-- Quick Actions Section -->
    <div class="quick-actions">
      <div class="container mx-auto px-4 py-6">
        <div class="grid grid-cols-2 gap-3">
          <a href="/chooseproducts" class="action-button primary">
            <Icon icon="mdi:calculator" width="20" height="20" />
            <span>Get Quote</span>
          </a>
          <a href="tel:800-268-9137" class="action-button secondary">
            <Icon icon="mdi:phone" width="20" height="20" />
            <span>Call Now</span>
          </a>
        </div>
      </div>
    </div>

    <!-- Visual Divider -->
    <div class="section-divider"></div>

    <!-- Collapsible Sections -->
    <div class="collapsible-sections">
      <div class="container mx-auto px-4 py-2">
        
        <!-- Insurance Products -->
        <div class="section-toggle">
          <button @click="toggleSection('products')" class="section-header">
            <span>Insurance Products</span>
            <Icon :icon="openSections.products ? 'mdi:chevron-up' : 'mdi:chevron-down'" width="20" height="20" />
          </button>
          <transition name="slide">
            <div v-if="openSections.products" class="section-content">
              <a href="/products/autoinsurance" class="section-link">Auto Insurance</a>
              <a href="/products/homeinsurance" class="section-link">Home Insurance</a>
              <a href="/products/lifeinsurance" class="section-link">Life Insurance</a>
              <a href="/products/businessinsurance" class="section-link">Business Insurance</a>
              <a href="/products/floodinsurance" class="section-link">Flood Insurance</a>
              <a href="/products/umbrellainsurance" class="section-link">Umbrella Insurance</a>
              <a href="/products/medicare" class="section-link">Medicare</a>
            </div>
          </transition>
        </div>

        <!-- Customer Support -->
        <div class="section-toggle">
          <button @click="toggleSection('support')" class="section-header">
            <span>Customer Support</span>
            <Icon :icon="openSections.support ? 'mdi:chevron-up' : 'mdi:chevron-down'" width="20" height="20" />
          </button>
          <transition name="slide">
            <div v-if="openSections.support" class="section-content">
              <a href="/customerservice/carrier-directory/make-claim" class="section-link">File a Claim</a>
              <a href="/customerservice/carrier-directory/make-payment" class="section-link">Make a Payment</a>
              <a href="/policy-search" class="section-link">Policy Search</a>
              <a href="/customerservice" class="section-link">Customer Service</a>
              <a href="/aboutus" class="section-link">About Us</a>
              <a @click="showMemberCompanies = true" class="section-link">Member Companies</a>
            </div>
          </transition>
        </div>

        <!-- Contact & Legal -->
        <div class="section-toggle">
          <button @click="toggleSection('contact')" class="section-header">
            <span>Contact & Legal</span>
            <Icon :icon="openSections.contact ? 'mdi:chevron-up' : 'mdi:chevron-down'" width="20" height="20" />
          </button>
          <transition name="slide">
            <div v-if="openSections.contact" class="section-content">
              <div class="contact-info">
                <div class="contact-item-mobile">
                  <Icon icon="mdi:phone" width="16" height="16" />
                  <a href="tel:800-268-9137">800-268-9137</a>
                </div>
                <p class="text-xs opacity-80 mt-1 mb-2">Text START to 800-268-9137 to receive SMS updates. Msg &amp; data rates may apply. Reply STOP to cancel.</p>
                <div class="contact-item-mobile">
                  <Icon icon="mdi:email" width="16" height="16" />
                  <a href="mailto:customerservice@insuranceexpress.com">Email Us</a>
                </div>
                <div class="contact-item-mobile">
                  <Icon icon="mdi:map-marker" width="16" height="16" />
                  <a href="https://maps.google.com/?q=2005+Vista+Parkway+Suite+200,+West+Palm+Beach,+FL+33411" target="_blank" rel="noopener noreferrer">
                    2005 Vista Parkway Suite 200<br>West Palm Beach, FL 33411
                  </a>
                </div>
              </div>
              <div class="legal-links">
                <a @click="showPrivacyPolicy = true" class="section-link">Privacy Policy</a>
                <a @click="showTermsOfUse = true" class="section-link">Terms of Use</a>
                <a href="/sitemap" class="section-link">Sitemap</a>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Bottom Bar -->
    <div class="footer-bottom-mobile">
      <div class="container mx-auto px-4 py-6">
        <div class="text-center">
          <p class="text-white text-sm opacity-90">
            © {{ new Date().getFullYear() }} Insurance Express
          </p>
        </div>
      </div>
    </div>

    <!-- Member Companies Modal -->
    <transition name="modal">
      <div v-if="showMemberCompanies" class="modal-overlay" @click="showMemberCompanies = false">
        <div class="modal-content-mobile" @click.stop>
          <button class="modal-close" @click="showMemberCompanies = false">✕</button>
          <h2 class="text-xl font-bold mb-4">Our Member Companies</h2>
          <div class="space-y-3">
            <a v-for="company in memberCompanies" :key="company.name" 
               :href="company.link" 
               target="_blank" 
               rel="noopener noreferrer"
               class="member-company-card-mobile">
              <h3 class="font-semibold text-gray-900 text-sm">{{ company.name }}</h3>
              <p class="text-xs text-gray-600">{{ company.description }}</p>
            </a>
          </div>
        </div>
      </div>
    </transition>

    <!-- Privacy Policy Modal -->
    <transition name="modal">
      <div v-if="showPrivacyPolicy" class="modal-overlay" @click="showPrivacyPolicy = false">
        <div class="modal-content-mobile" @click.stop>
          <button class="modal-close" @click="showPrivacyPolicy = false">✕</button>
          <h2 class="text-xl font-bold mb-4">Privacy Policy</h2>
          <div class="text-xs text-gray-700 space-y-2 max-h-64 overflow-y-auto">
            <p>Last updated: {{ new Date().toLocaleDateString() }}</p>
            <p>At Insurance Express, we are committed to protecting your personal information and respecting your privacy.</p>
            <p>For the complete Privacy Policy, please visit our website or contact us directly.</p>
            <div class="mt-4 pt-4 border-t text-center">
              <a href="tel:800-268-9137" class="text-blue-500">800-268-9137</a>
            </div>
          </div>
        </div>
      </div>
    </transition>

    <!-- Terms of Use Modal -->
    <transition name="modal">
      <div v-if="showTermsOfUse" class="modal-overlay" @click="showTermsOfUse = false">
        <div class="modal-content-mobile" @click.stop>
          <button class="modal-close" @click="showTermsOfUse = false">✕</button>
          <h2 class="text-xl font-bold mb-4">Terms of Use</h2>
          <div class="text-xs text-gray-700 space-y-2 max-h-64 overflow-y-auto">
            <p>By accessing and using Insurance Express's website and services, you agree to be bound by these Terms of Use.</p>
            <p>For complete Terms of Use, please visit our website or contact us directly.</p>
            <div class="mt-4 pt-4 border-t text-center">
              <a href="tel:800-268-9137" class="text-blue-500">800-268-9137</a>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </footer>
</template>

<style scoped>
/* Modern Mobile Footer - 2025 Best Practices */
.footer-mobile {
  background: linear-gradient(180deg, #ffffff 0%, #f8f9fa 100%);
  border-top: 1px solid #e5e7eb;
}

/* Trust Section */
.trust-section {
  background-color: white;
}

.trust-badge-mobile {
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 8px 12px;
  font-size: 11px;
  color: #475569;
  font-weight: 600;
  white-space: nowrap;
  transition: all 0.2s ease;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
}

.trust-badge-mobile:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.trust-badge-mobile svg {
  color: #10b981;
  margin-right: 6px;
  flex-shrink: 0;
}

/* Section Dividers */
.section-divider {
  height: 8px;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(219, 9, 8, 0.1) 20%, 
    rgba(219, 9, 8, 0.05) 50%, 
    rgba(219, 9, 8, 0.1) 80%, 
    transparent 100%
  );
  position: relative;
}

.section-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 40px;
  height: 2px;
  background: linear-gradient(90deg, transparent, var(--color-primary, #DB0908), transparent);
  opacity: 0.3;
}

/* Quick Actions */
.quick-actions {
  background-color: white;
}

.action-button {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 16px;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 14px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid transparent;
}

.action-button.primary {
  background: linear-gradient(135deg, var(--color-primary, #DB0908) 0%, #c53030 100%);
  color: white;
}

.action-button.primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(219, 9, 8, 0.3);
}

.action-button.secondary {
  background: linear-gradient(135deg, #4a5568 0%, #2d3748 100%);
  color: white;
}

.action-button.secondary:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(74, 85, 104, 0.3);
}

/* Collapsible Sections */
.collapsible-sections {
  background-color: #f8f9fa;
}

.section-toggle {
  border-bottom: 1px solid #e5e7eb;
}

.section-toggle:last-child {
  border-bottom: none;
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 20px 0;
  background: none;
  border: none;
  font-size: 16px;
  font-weight: 600;
  color: #1f2937;
  text-align: left;
  transition: color 0.2s ease;
}

.section-header:hover {
  color: var(--color-primary, #DB0908);
}

.section-header svg {
  transition: transform 0.2s ease;
  color: #6b7280;
}

.section-header:hover svg {
  color: var(--color-primary, #DB0908);
}

.section-content {
  padding-bottom: 20px;
}

.section-link {
  display: block;
  padding: 12px 0;
  color: #6b7280;
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;
  border-radius: 4px;
  margin: 0 -4px;
  padding-left: 4px;
}

.section-link:hover {
  color: var(--color-primary, #DB0908);
  background-color: rgba(219, 9, 8, 0.05);
  padding-left: 8px;
}

/* Contact Info */
.contact-info {
  margin-bottom: 20px;
}

.contact-item-mobile {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  font-size: 14px;
}

.contact-item-mobile svg {
  color: var(--color-primary, #DB0908);
  flex-shrink: 0;
}

.contact-item-mobile a {
  color: var(--color-primary, #DB0908);
  text-decoration: none;
  font-weight: 500;
  transition: opacity 0.2s ease;
}

.contact-item-mobile a:hover {
  opacity: 0.8;
}

.legal-links {
  border-top: 1px solid #e5e7eb;
  padding-top: 16px;
}

/* Bottom Bar */
.footer-bottom-mobile {
  background: linear-gradient(135deg, #1f2937 0%, #111827 100%);
  color: white;
}

/* Modal Improvements */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-end;
  z-index: 50;
}

.modal-content-mobile {
  background-color: white;
  border-radius: 20px 20px 0 0;
  width: 100%;
  max-width: 100%;
  position: relative;
  padding: 24px;
  max-height: 80vh;
  overflow-y: auto;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);
}

.modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  background: #f3f4f6;
  border: none;
  border-radius: 50%;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  color: #6b7280;
  cursor: pointer;
  transition: all 0.2s ease;
}

.modal-close:hover {
  background: #e5e7eb;
  color: #374151;
}

.member-company-card-mobile {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 16px;
  text-decoration: none;
  display: block;
  transition: all 0.2s ease;
}

.member-company-card-mobile:hover {
  border-color: var(--color-primary, #DB0908);
  background: linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%);
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Smooth Transitions */
.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
}

.slide-enter-from,
.slide-leave-to {
  max-height: 0;
  opacity: 0;
}

.slide-enter-to,
.slide-leave-from {
  max-height: 400px;
  opacity: 1;
}

.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.3s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-content-mobile,
.modal-leave-active .modal-content-mobile {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.modal-enter-from .modal-content-mobile,
.modal-leave-to .modal-content-mobile {
  transform: translateY(100%);
}

/* Responsive Design */
@media (max-width: 480px) {
  .trust-badge-mobile {
    font-size: 10px;
    padding: 6px 10px;
  }
  
  .action-button {
    padding: 14px;
    font-size: 13px;
  }
  
  .section-header {
    padding: 16px 0;
    font-size: 15px;
  }
  
  .modal-content-mobile {
    padding: 20px;
    border-radius: 16px 16px 0 0;
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .footer-mobile {
    background: linear-gradient(180deg, #1f2937 0%, #111827 100%);
    border-top-color: #374151;
  }
  
  .trust-section,
  .quick-actions {
    background-color: #1f2937;
  }
  
  .collapsible-sections {
    background-color: #111827;
  }
  
  .section-header {
    color: #f9fafb;
  }
  
  .section-link {
    color: #d1d5db;
  }
}
</style>

<script>
import { Icon } from '@iconify/vue';

export default {
  name: 'AppFooterMobile',
  components: {
    Icon
  },
  data() {
    return {
      openSections: {
        products: false,
        support: false,
        contact: false
      },
      showPrivacyPolicy: false,
      showTermsOfUse: false,
      showMemberCompanies: false,
      memberCompanies: [
        { name: 'Rocket Flood', link: 'https://rocketflood.com/', description: 'Flood insurance specialists' },
        { name: 'Rocket MGA', link: 'https://rocketmga.com/', description: 'Managing general agency services' },
        { name: 'Str8line Group', link: 'http://www.str8linegroup.com/', description: 'Insurance solutions provider' },
        { name: 'QuoteSlash', link: 'https://quoteslash.com/', description: 'Digital insurance platform' },
        { name: 'Protego VIP', link: 'https://protegovip.com/', description: 'VIP protection services' },
        { name: 'TheLeadExchange', link: 'https://theleadexchange.com/', description: 'Lead generation platform' },
        { name: 'NuBuild', link: 'https://nubuildinsurance.com/', description: 'Construction insurance' },
        { name: 'The IE Pipeline', link: 'https://iepipeline.com/', description: 'Insurance pipeline solutions' },
        { name: 'Graham Newton & McMahon Inc.', link: 'https://gnminsurance.com/', description: 'Professional liability insurance' },
        { name: 'Noble Care', link: 'https://www.noblecareinsurance.com/', description: 'Healthcare insurance solutions' }
      ]
    };
  },
  methods: {
    toggleSection(section) {
      this.openSections[section] = !this.openSections[section];
    }
  }
};
</script> 